import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Building2,
  Upload,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  List
} from 'lucide-react';

const Sidebar = ({ user, logout, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);

  // Get dashboard path based on user role
  const getDashboardPath = () => {
    if (user?.role === 'auditor') return '/auditor';
    if (user?.role === 'admin') {
      return user.tenant_id === null ? '/super-admin' : '/tenant-admin';
    }
    if (user?.role === 'vendor_user') return '/vendor';
    return '/dashboard';
  };

  // Menu items based on role
  const getMenuItems = () => {
    const isSuperAdmin = user?.role === 'admin' && user?.tenant_id === null;
    const isTenantAdmin = user?.role === 'admin' && user?.tenant_id !== null;
    const isAuditor = user?.role === 'auditor';

    // Base items - Dashboard only for super admin, tenant admin, and auditor
    const baseItems = [
      { icon: LayoutDashboard, label: 'Dashboard', path: getDashboardPath() },
    ];

    // Only show Upload Batch and Batches for non-super-admin, non-tenant-admin, and non-auditor users
    if (!isSuperAdmin && !isTenantAdmin && !isAuditor) {
      baseItems.push({ icon: List, label: 'Batches', path: '/batches' });
      baseItems.push({ icon: Upload, label: 'Upload Batch', path: '/batch/create' });
    }

    // Super Admin items
    const superAdminItems = [
      { icon: Building2, label: 'Tenants', path: '/admin/tenants' },
      { icon: Users, label: 'Users', path: '/admin/users' },
    ];

    // Tenant Admin items
    const tenantAdminItems = [
      { icon: Users, label: 'Manage Users', path: '/admin/users' },
      { icon: Briefcase, label: 'Jobs', path: '/admin/jobs' },
    ];

    if (isSuperAdmin) {
      return [...baseItems, ...superAdminItems];
    }

    if (isTenantAdmin) {
      return [...baseItems, ...tenantAdminItems];
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const styles = `
    .sidebar {
      width: var(--sidebar-width);
      height: 100vh;
      position: fixed;
      left: 0;
      top: 0;
      background: var(--gradient-sidebar);
      border-right: 1px solid hsl(var(--sidebar-bg) / 0.8);
      display: flex;
      flex-direction: column;
      transition: width 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      z-index: 100;
    }

    .sidebar.collapsed {
      width: var(--sidebar-collapsed);
    }

    .sidebar-header {
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      border-bottom: 1px solid hsl(var(--sidebar-hover));
    }

    .sidebar-logo {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-md);
      background: var(--gradient-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 800;
      color: white;
      box-shadow: var(--shadow-glow);
      position: relative;
      overflow: hidden;
      flex-shrink: 0;
    }

    .sidebar-brand {
      font-size: 12px;
      font-weight: 800;
      color: white;
      white-space: normal;
      line-height: 1.3;
      letter-spacing: 0.5px;
      word-break: keep-all;
      flex: 1;
    }

    .sidebar-menu {
      flex: 1;
      padding: 20px 12px;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .sidebar-menu::-webkit-scrollbar {
      width: 4px;
    }

    .sidebar-menu::-webkit-scrollbar-thumb {
      background: hsl(var(--sidebar-hover));
      border-radius: 4px;
    }

    .menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      margin-bottom: 4px;
      border-radius: var(--radius-md);
      color: hsl(var(--sidebar-text));
      text-decoration: none;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      position: relative;
      white-space: nowrap;
    }

    .menu-item:hover {
      background: hsl(var(--sidebar-hover));
      color: white;
      transform: translateX(4px);
    }

    .menu-item.active {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--shadow-glow);
    }

    .menu-item.active::before {
      content: '';
      position: absolute;
      left: -8px;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background: white;
      border-radius: 2px;
    }

    .menu-item-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .menu-item-label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sidebar-footer {
      padding: 20px;
      border-top: 1px solid hsl(var(--sidebar-hover));
    }

    .user-profile {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: hsl(var(--sidebar-hover) / 0.5);
      border-radius: var(--radius-md);
      margin-bottom: 12px;
    }

    .user-avatar {
      width: 36px;
      height: 36px;
      border-radius: var(--radius-md);
      background: var(--gradient-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 700;
      color: white;
      flex-shrink: 0;
    }

    .user-info {
      flex: 1;
      overflow: hidden;
    }

    .user-name {
      font-size: 13px;
      font-weight: 600;
      color: white !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-role {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7) !important;
      text-transform: capitalize;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .logout-button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 16px;
      background: transparent;
      border: 1px solid hsl(var(--sidebar-hover));
      border-radius: var(--radius-md);
      color: hsl(var(--sidebar-text));
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;
    }

    .logout-button:hover {
      background: hsl(var(--destructive) / 0.1);
      border-color: hsl(var(--destructive));
      color: hsl(var(--destructive));
    }

    .collapse-button {
      position: absolute;
      right: -12px;
      top: 32px;
      width: 24px;
      height: 24px;
      background: var(--gradient-primary);
      border: 2px solid hsl(var(--sidebar-bg));
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: var(--shadow-glow);
      transition: all 0.3s;
      z-index: 10;
    }

    .collapse-button:hover {
      transform: scale(1.1);
    }

    .collapse-button svg {
      width: 14px;
      height: 14px;
      color: white;
    }

    @media (max-width: 768px) {
      .sidebar {
        transform: translateX(-100%);
      }

      .sidebar.collapsed {
        transform: translateX(0);
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Collapse Button */}
        <button
          className="collapse-button"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </button>

        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo shimmer-overlay">C</div>
          <AnimatePresence>
            {!collapsed && (
              <motion.div
                className="sidebar-brand"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
              >
                CVR VALIDATION SYSTEM
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Menu */}
        <div className="sidebar-menu">
          {menuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                if (item.path === '/batch/create' && user?.is_active === false) {
                  setShowDeactivatedModal(true);
                  return;
                }
                navigate(item.path);
              }}
              title={collapsed ? item.label : ''}
            >
              <item.icon className="menu-item-icon" />
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    className="menu-item-label"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">

          <button className="logout-button" onClick={logout} title="Logout">
            <LogOut size={16} />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Deactivated account modal */}
      {showDeactivatedModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }} onClick={() => setShowDeactivatedModal(false)}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '90%',
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#991b1b', marginBottom: '8px' }}>
              Account Deactivated
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
              Your account has been deactivated. You can view your existing batches but cannot upload new ones. Please contact your administrator for assistance.
            </p>
            <button
              onClick={() => setShowDeactivatedModal(false)}
              style={{
                padding: '10px 32px', borderRadius: '8px', border: 'none',
                background: '#dc2626', color: 'white', fontSize: '14px',
                fontWeight: '600', cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
